<template>
  <div class="hidden" />
</template>

<script>
import _ from 'lodash'
import { RestApiClient } from 'ifield-core-components'
import NdjsonClient from '@/lib/NdjsonClient.js'
import TrackRenderer from '@/js/map-renderer/TrackRenderer.js'
import ColorUtil from '@/js/util/ColorUtil.js'
import { HIGHLIGHT_SCALE_FACTOR } from '@/constants.js'
import VARIABLES from '@/assets/scss/variables.module.scss'
import { toServerTz } from '@/js/TimezoneHelper'
import { useHandleApiError } from '@/composables/UseHandleApiError.js'
import { INVISIBLE_USER_OPACITY_RATIO } from '@/constants.js'

// 使用30色
const COLORS = [
  VARIABLES.ENTITY_COLOR_RED,
  VARIABLES.ENTITY_COLOR_NAVY,
  VARIABLES.ENTITY_COLOR_ORANGE,
  VARIABLES.ENTITY_COLOR_GREEN,
  VARIABLES.ENTITY_COLOR_WINE_RED,
  VARIABLES.ENTITY_COLOR_BLUE,
  VARIABLES.ENTITY_COLOR_RED_PURPLE,
  VARIABLES.ENTITY_COLOR_LIGHT_GREEN,
  VARIABLES.ENTITY_COLOR_GOLD,
  VARIABLES.ENTITY_COLOR_PURPLE,
  VARIABLES.ENTITY_COLOR_PEACH_PINK,
  VARIABLES.ENTITY_COLOR_BLUE_GREEN,
  VARIABLES.ENTITY_COLOR_BROWN,
  VARIABLES.ENTITY_COLOR_LIGHT_BLUE,
  VARIABLES.ENTITY_COLOR_ORANGE_RED,
  VARIABLES.ENTITY_COLOR_DEEP_BLUE_GREEN,
  VARIABLES.ENTITY_COLOR_DEEP_PINK,
  VARIABLES.ENTITY_COLOR_LIME_GREEN,
  VARIABLES.ENTITY_COLOR_YELLOW,
  VARIABLES.ENTITY_COLOR_BLUE_PURPLE,
  VARIABLES.ENTITY_COLOR_PINK,
  VARIABLES.ENTITY_COLOR_FRESH_GREEN,
  VARIABLES.ENTITY_COLOR_WISTERIA_PURPLE,
  VARIABLES.ENTITY_COLOR_EMERALD,
  VARIABLES.ENTITY_COLOR_MANDARIN_ORANGE,
  VARIABLES.ENTITY_COLOR_GOLDEN_YELLOW,
  VARIABLES.ENTITY_COLOR_ROSE_PINK,
  VARIABLES.ENTITY_COLOR_SKY_BLUE,
  VARIABLES.ENTITY_COLOR_PASSION_RED,
  VARIABLES.ENTITY_COLOR_ROYAL_BLUE,
]

// 開始/終了地点のアイコンサイズ
const EDGE_ICON_SIZE = 24

// 開始/終了地点のアイコンの最大サイズ（ハイライト時）
const MAX_EDGE_ICON_SIZE = EDGE_ICON_SIZE * HIGHLIGHT_SCALE_FACTOR

/**
 * 指定キーの表示状態取得
 * @param {Boolean|Object} visible 表示状態
 * @param {String} key キー
 * @returns {Boolean} 表示状態
 */
const getVisibleByKey = (visible, key) => {
  return typeof visible === 'boolean' ? visible : visible[key]
}

// レンダラーのfactoryインスタンス
let rendererFactory = null

/**
 * 測位始点、終点アイコンSVGパス生成
 * @param {String} type 始点の場合 'start'、終点の場合 'end'
 * @param {String} color fill に指定するhex形式カラー
 * @returns {String} SVGパス
 */
const getIconSvgPath = (type, color) => `
  <svg width="${MAX_EDGE_ICON_SIZE}" height="${MAX_EDGE_ICON_SIZE}" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clip-path="url(#clip0_109_86)">
      <path d="M11 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z" fill="#${color}"/>
      ${
        type === 'start'
          ? `<path d="M12.2057 19.5086C8.00569 19.5086 6.83997 17.4686 6.27426 15.6857C6.23997 15.5143 6.20569 15.3943 6.20569 15.2743C6.20569 14.64 6.68569 14.0915 7.38855 13.9372C7.59426 13.9029 7.69712 13.8857 7.83426 13.8857C8.50283 13.8857 9.0514 14.2629 9.23997 14.8457C9.5314 15.8743 9.95997 16.7829 12.12 16.7829C12.7371 16.7829 14.76 16.6629 14.76 15.0343C14.76 14.2629 14.5885 13.6972 12.3771 13.2686L10.9543 12.9772C8.77712 12.5486 6.56569 11.6915 6.56569 8.64003C6.56569 6.72003 7.9714 4.49146 11.9314 4.49146C15.6343 4.49146 16.8343 6.5486 17.2285 7.78288C17.28 7.88574 17.3143 8.05717 17.3143 8.2286C17.3143 8.86288 16.8343 9.41146 16.1314 9.53146C16.0114 9.5486 15.8571 9.58288 15.7028 9.58288C15.0685 9.58288 14.5543 9.24003 14.3314 8.69146C14.1257 8.00574 13.68 7.18288 11.9314 7.18288C10.4743 7.18288 9.56569 7.7486 9.56569 8.64003C9.56569 9.34288 9.71998 9.85717 11.6571 10.2343L12.9771 10.4915C15.4628 10.9886 17.7771 11.8972 17.7771 15.0343C17.7771 17.7086 15.5314 19.5086 12.1885 19.5086H12.2057Z" fill="white"/>`
          : `<path d="M12.4972 19.5085C8.41717 19.5085 5.79431 16.56 5.79431 12C5.79431 7.43997 8.4686 4.50854 12.4457 4.50854C15.8572 4.50854 17.3143 6.51426 17.88 7.71426C17.9829 7.90283 18.0343 8.10854 18.0343 8.34854C18.0343 8.91426 17.6572 9.4114 17.0743 9.63426C16.92 9.68569 16.68 9.75426 16.4229 9.75426C15.84 9.75426 15.3772 9.46283 15.1372 8.91426C14.6229 7.78283 13.8 7.26854 12.48 7.26854C10.2172 7.26854 8.8286 9.08569 8.8286 12.0171C8.8286 14.9485 10.2172 16.7485 12.5486 16.7485C14.0057 16.7485 15.4629 15.8571 15.4629 14.16V13.9543H13.3886C12.6 13.9543 12.0515 13.4057 12.0515 12.6C12.0515 11.7943 12.6 11.2114 13.3886 11.2114H16.1657C17.5372 11.2114 18.2229 11.9314 18.2229 13.3371V18.12C18.2229 18.9428 17.6743 19.4914 16.8515 19.4914C16.2 19.4914 15.7029 19.1657 15.5315 18.6343C14.6915 19.1828 13.5943 19.5257 12.4972 19.5257V19.5085Z" fill="white"/>`
      }
    </g>
    <defs>
      <clipPath id="clip0_109_86">
      <rect width="24" height="24" fill="white"/>
      </clipPath>
    </defs>
  </svg>
`

/**
 * SVGパスを含む dataURL 生成
 * @param {String} svg SVGパス
 * @returns {String} dataURL
 */
const svgToDataURL = (svg) =>
  `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`

export default {
  name: 'AppAnalyzeTrackLayerManager',
  inheritAttrs: false,
  props: {
    /**
     * 開始日時
     */
    from: {
      type: String,
      default: null,
    },
    /**
     * 終了日時
     */
    to: {
      type: String,
      default: null,
    },
    /**
     * 対象ユーザー
     */
    users: {
      type: Array,
      default: null,
    },
    /**
     * フロアID
     */
    indoorLevelId: {
      type: Number,
      default: null,
    },
    /**
     * レイヤー表示状態
     */
    visible: {
      type: Object,
      default: () => ({ all: true, geometry: true, user: true }),
    },
    /**
     * 図形表示設定
     */
    setting: {
      type: Object,
      default: () => ({
        pointRadius: 0,
        pointLineWidth: 0,
        lineWidth: 0,
      }),
    },
    /**
     * ハイライト対象のユーザーID
     */
    highlightedUserId: {
      type: Number,
      default: null,
    },
  },
  setup(props, context) {
    const { handleApiError } = useHandleApiError(props, context)
    return { handleApiError }
  },
  data: function () {
    return {
      // 図形表示状態
      geometryVisible: {
        point: true,
        line: true,
        start: true,
        end: true,
      },
      // 初回描画済み
      isExecuted: false,
      // hover中地物
      hoverFeature: null,
    }
  },
  computed: {
    /**
     * 対象ユーザーID
     * @type {Array.<Number>}
     */
    userIds: function () {
      if (!this.users || !this.users.length) return null
      return this.users.map((u) => u.id)
    },
    /**
     * APIクライアント用パラメータ
     * @type {Object}
     */
    paramsForClient: function () {
      return {
        from: this.from,
        to: this.to,
        userId: this.userIds,
        indoorLevelId: this.indoorLevelId,
      }
    },
    /**
     * 使用10色(RGB)
     * @type {Array.<Array.<Number>>}
     */
    rgbColors: function () {
      return COLORS.map((c) => ColorUtil.hexToRgb(c))
    },
    /**
     * ユーザーのindexMap
     * @type {Map}
     */
    userIndexMap: function () {
      if (!this.userIds) return null
      return new Map(this.users.map(({ id }, index) => [id, index]))
    },
    /**
     * visibleなユーザーIDのSet
     * @type {Set}
     */
    visibleUserIdSet: function () {
      let visibleIds = []
      if (this.userIds) {
        visibleIds = this.userIds.filter((id) =>
          getVisibleByKey(this.visible.user, `${id}`)
        )
      }
      return new Set(visibleIds)
    },
    /**
     * 加工済みユーザーデータ
     * - 色の割り当て
     * @type {Array.<Object>}
     */
    usersModified: function () {
      if (!this.userIndexMap) return null
      return this.users.map((user) => {
        return {
          ...user,
          color: this.$_getUserColorById(user.id),
        }
      })
    },
    /**
     * deck.glで色を取得するAccessor
     * @type {Function}
     */
    getColor: function () {
      return (d) => this.$_getUserColorById(d.user_id) || d.color
    },
    /**
     * レイヤーメニュー用データ
     * @type {Object}
     */
    layerMenuData: function () {
      return {
        id: TrackRenderer.LAYER_ID,
        visible: this.visible.all,
        name: this.$t('appAnalyzeTrackLayerManager.layerName'),
      }
    },
    /**
     * 凡例用情報
     * @type {Object}
     */
    legendData: function () {
      return { users: this.usersModified }
    },
  },
  created: function () {
    this.$_updateGeometryVisible()
  },
  mounted: function () {
    this.$_execute()
  },
  beforeDestroy: function () {
    // リクエスト中断
    this.client.abort()
  },
  watch: {
    paramsForClient: {
      handler: async function () {
        await this.client.abort()
        this.$_createRenderer()
        this.$_emitLayerMenuDataChanged()
      },
      deep: true,
    },
    'visible.all': function () {
      this.$_createRenderer({
        onDataLoad: () => this.$_hideLoading(),
      })
    },
    'visible.user': function () {
      this.$_createRenderer({
        onDataLoad: () => this.$_hideLoading(),
      })
    },
    'visible.geometry': function () {
      this.$_updateGeometryVisible()
    },
    highlightedUserId: function () {
      this.isExecuted &&
        this.$_createRenderer({
          onDataLoad: () => this.$_hideLoading(),
        })
    },
    setting: function () {
      // 再描画
      this.isExecuted &&
        this.$_createRenderer({
          onDataLoad: () => this.$_hideLoading(),
        })
    },
    layerMenuData: function () {
      this.$_emitLayerMenuDataChanged()
    },
    legendData: {
      handler: function () {
        this.$_emitLegendDataChanged()
      },
      deep: true,
    },
    hoverFeature: function () {
      this.$_emitHoverFeatureChanged()
    },
  },
  methods: {
    /**
     * ユーザーの表示状態に応じて色と不透明度を返す
     * @param {Object} d データオブジェクト
     * @returns {Array.<Number>} 色 ([r, g, b, a])
     */
    $_getColorWithOpacity(d) {
      const color = this.$_getUserColorById(d.user_id) || d.color
      if (!color) return [0, 0, 0, 0] // Fallback
      const alpha = this.visibleUserIdSet.has(d.user_id)
        ? 255
        : 255 * INVISIBLE_USER_OPACITY_RATIO
      return [color[0], color[1], color[2], alpha]
    },

    /**
     * 実行
     */
    $_execute: async function () {
      this.client = new NdjsonClient()
      rendererFactory = new TrackRenderer()
      this.$_createRenderer()
      this.$_emitLegendDataChanged()
      this.$_emitLayerMenuDataChanged()
      this.isExecuted = true
    },

    /**
     * ロケーションデータ取得
     * @yields {Array.<Object>} 統計用ロケーションデータ
     */
    $_getLocations: async function* () {
      const url = RestApiClient.createApiUrl('location/report')
      this.$_addQueryParameters(url)

      try {
        yield* this.client.getData(url)
      } catch (error) {
        this.handleApiError(error)
        return
      }
    },

    /**
     * パラメータを追加する
     * @param {URL} url URL
     */
    $_addQueryParameters: function (url) {
      if (this.from) {
        url.searchParams.append('from', toServerTz(this.from))
      }
      if (this.to) {
        url.searchParams.append('to', toServerTz(this.to))
      }
      if (this.userIds) {
        const userId = Array.isArray(this.userIds)
          ? this.userIds.join(',')
          : this.userIds
        url.searchParams.append('user_id', userId)
      }
      if (this.indoorLevelId) {
        url.searchParams.append('indoor_level_id', this.indoorLevelId)
      }
    },

    /**
     * ユーザーIDから色を取得する
     * @param {Number}
     * @returns {Array.<Number>} 色 ([r, g, b])
     */
    $_getUserColorById: function (id) {
      if (!this.userIndexMap) return null
      return this.rgbColors[this.userIndexMap.get(id) % this.rgbColors.length]
    },

    /**
     * 始点、終点アイコンSVGのURL取得
     * @param {Object} d IconLayerのAccessor
     * @param {String} type 始点 or 終点の指定
     * @return {String} 対象SVGデータのパス
     */
    $_getEdgeIcon: function (d, type) {
      const hexColor = ColorUtil.rgbToHex(this.getColor(d)).replace('#', '')
      return svgToDataURL(getIconSvgPath(type, hexColor))
    },

    /**
     * レンダラー作成
     * @param {Object} overrideProps 上書きするprops
     * @returns {Object} レンダラー
     */
    $_createRenderer: async function (overrideProps = {}) {
      const dataSource = this.$_getLocations
      const props = {
        visible: this.visible.all,
        pickable: true,
        onHover: ({ object }) => {
          this.hoverFeature = object
        },
        onDataLoad: (data) => {
          if (!data) return // エラーはハンドリング済み
          if (!data.length && !this.$alert.isVisible()) {
            this.$_emitNoLocationFound()
          }
          this.$_hideLoading()
        },
        updateTriggers: {
          getRadius: [this.highlightedUserId, this.setting.pointRadius],
          getLineWidth: [
            this.highlightedUserId,
            this.setting.pointLineWidth,
            this.setting.lineWidth,
          ],
        },
        transitions: {
          getRadius: 100,
          getLineWidth: 100,
          getWidth: 100,
        },
        highlightedUserId: this.highlightedUserId,
        ...overrideProps,
        _subLayerProps: {
          point: {
            radiusUnits: 'pixels',
            lineWidthUnits: 'pixels',
            getRadius: (d) =>
              d.user_id === this.highlightedUserId
                ? this.setting.pointRadius * HIGHLIGHT_SCALE_FACTOR
                : this.setting.pointRadius,
            stroked: true,
            visible: this.visible.all && this.geometryVisible.point,
            pickable: true,
            getLineWidth: (d) =>
              d.user_id === this.highlightedUserId
                ? this.setting.pointLineWidth * HIGHLIGHT_SCALE_FACTOR
                : this.setting.pointLineWidth,
            getLineColor: this.$_getColorWithOpacity,
            getFillColor: [255, 255, 255, 0],
            getPosition: (d) => [d.lng, d.lat],
            updateTriggers: {
              getLineColor: [this.visibleUserIdSet],
            },
          },
          line: {
            widthUnits: 'pixels',
            capRounded: true,
            jointRounded: true,
            visible: this.visible.all && this.geometryVisible.line,
            pickable: false,
            getWidth: (d) =>
              d.user_id === this.highlightedUserId
                ? this.setting.lineWidth * HIGHLIGHT_SCALE_FACTOR
                : this.setting.lineWidth,
            getColor: this.$_getColorWithOpacity,
            getPath: (d) => d.data.map((v) => [v.lng, v.lat]),
            updateTriggers: {
              getColor: [this.visibleUserIdSet],
            },
          },
          start: {
            visible: this.visible.all && this.geometryVisible.start,
            getIcon: (d) => ({
              url: this.$_getEdgeIcon(d, 'start'),
              width: MAX_EDGE_ICON_SIZE,
              height: MAX_EDGE_ICON_SIZE,
            }),
            getPosition: (d) => [d.lng, d.lat],
            getSize: (d) =>
              d.user_id === this.highlightedUserId
                ? EDGE_ICON_SIZE * HIGHLIGHT_SCALE_FACTOR
                : EDGE_ICON_SIZE,
            pickable: true,
            getColor: this.$_getColorWithOpacity,
            updateTriggers: {
              getColor: [this.visibleUserIdSet],
            },
          },
          end: {
            visible: this.visible.all && this.geometryVisible.end,
            getIcon: (d) => ({
              url: this.$_getEdgeIcon(d, 'end'),
              width: MAX_EDGE_ICON_SIZE,
              height: MAX_EDGE_ICON_SIZE,
            }),
            getPosition: (d) => [d.lng, d.lat],
            getSize: (d) =>
              d.user_id === this.highlightedUserId
                ? EDGE_ICON_SIZE * HIGHLIGHT_SCALE_FACTOR
                : EDGE_ICON_SIZE,
            pickable: true,
            getColor: this.$_getColorWithOpacity,
            updateTriggers: {
              getColor: [this.visibleUserIdSet],
            },
          },
        },
      }
      const renderer = rendererFactory.createRenderer(dataSource, props)
      this.$_emitRendererChangedEvent(renderer)
      this.$_showLoading()
    },

    /**
     * 図形種類の表示状態更新
     */
    $_updateGeometryVisible: function () {
      const newVisible = {
        ...this.geometryVisible,
        point: getVisibleByKey(this.visible.geometry, 'point'),
        line: getVisibleByKey(this.visible.geometry, 'line'),
        start: getVisibleByKey(this.visible.geometry, 'start'),
        end: getVisibleByKey(this.visible.geometry, 'end'),
      }

      if (!_.isEqual(this.geometryVisible, newVisible)) {
        this.geometryVisible = newVisible
        this.isExecuted &&
          this.$_createRenderer({
            onDataLoad: () => this.$_hideLoading(),
          })
      }
    },

    /**
     * レンダラー変更イベントを発行する
     */
    $_emitRendererChangedEvent: function (renderer) {
      /**
       * レンダラー変更イベント
       * @param {Object} renderer 分析レイヤーのレンダラー
       */
      this.$emit('renderer-changed', renderer)
    },

    /**
     * レイヤーメニューデータ変更イベントを発行する
     */
    $_emitLayerMenuDataChanged: function () {
      /**
       * レイヤーメニューデータ変更イベント
       * @param {Array.<Object>} layerMenuData レイヤーメニューデータ
       */
      this.$emit('layer-menu-data-changed', [this.layerMenuData])
    },

    /**
     * 凡例データ変更イベント発行
     */
    $_emitLegendDataChanged: function () {
      /**
       * 凡例データ変更イベント
       * @param {Object} legendData 凡例データ
       */
      this.$emit('legend-data-changed', this.legendData)
    },

    /**
     * ロケーションデータが無いイベントを発行
     */
    $_emitNoLocationFound: function () {
      /**
       * ロケーションデータが無いイベント
       */
      this.$emit('no-location-found')
    },

    /**
     * ローディング表示
     */
    $_showLoading: function () {
      this.$_emitIsLoadingChanged(true)
    },

    /**
     * ローディング非表示
     */
    $_hideLoading: function () {
      this.$_emitIsLoadingChanged(false)
    },

    /**
     * ローディング状態変更イベント発行
     * @param {Boolean} isLoading ロード中の場合はtrue
     */
    $_emitIsLoadingChanged: function (isLoading) {
      /**
       * ローディング状態変更イベント
       * @param {Boolean} isLoading ロード中の場合はtrue
       */
      this.$emit('is-loading-changed', isLoading)
    },

    /**
     * hover地物変更イベント発行
     */
    $_emitHoverFeatureChanged: function () {
      /**
       * hover地物変更イベント
       * @param {Object} hoverFeature hover地物
       */
      this.$emit('hover-feature-changed', this.hoverFeature)
    },
  },
}
</script>

<style lang="scss" scoped>
.hidden {
  display: none;
}
</style>
